import React, { useState, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import Tesseract from 'tesseract.js';
import { toStructured, renderPlain, renderMarkdown, renderJSON } from './OCRFormat';
import '../../styling/OCRViewer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export default function PDFViewer({ fileUrl, zoom = 1.5, lang = 'eng' }) {
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [progress, setProgress] = useState({ page: 0, total: 0, percent: 0 });
  const [ocrScale, setOcrScale] = useState(2.5);
  const [threshold, setThreshold] = useState(180);
  const [rawText, setRawText] = useState('');
  const canvasRef = useRef(null);

  const onLoadSuccess = useCallback(({ numPages }) => setNumPages(numPages), []);
  const onLoadError = useCallback(() => setError('Error loading PDF.'), []);

  const runOCR = useCallback(async () => {
    if (!fileUrl) return;
    setIsExtracting(true);
    setRawText(''); setError(null);

    try {
      const loadingTask = pdfjs.getDocument(fileUrl);
      const pdf = await loadingTask.promise;

      let rawOut = [];
      let mdOut = [];
      let jsonOut = [];

      for (let i = 1; i <= pdf.numPages; i++) {
        setProgress({ page: i, total: pdf.numPages, percent: 0 });
        const page = await pdf.getPage(i);

        // ~300 DPI equivalent rendering for OCR
        const targetDpi = 300;
        const scale = (targetDpi / 72) * (ocrScale / 2);
        const viewport = page.getViewport({ scale, intent: 'print' });

        if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.imageSmoothingEnabled = false;
        canvas.width = viewport.width; canvas.height = viewport.height;
        await page.render({ canvasContext: ctx, viewport }).promise;

        // Threshold (if provided) and OCR
        const cleaned = (typeof threshold === 'number')
          ? (() => {
              const c = document.createElement('canvas');
              c.width = canvas.width; c.height = canvas.height;
              const ictx = c.getContext('2d', { willReadFrequently: true });
              ictx.drawImage(canvas, 0, 0);
              const img = ictx.getImageData(0, 0, c.width, c.height);
              const d = img.data;
              for (let p = 0; p < d.length; p += 4) {
                const y = 0.299*d[p] + 0.587*d[p+1] + 0.114*d[p+2];
                const v = y > threshold ? 255 : 0;
                d[p]=d[p+1]=d[p+2]=v;
              }
              ictx.putImageData(img, 0, 0);
              return c;
            })()
          : canvas;

        const dataUrl = cleaned.toDataURL('image/png');
        const result = await Tesseract.recognize(dataUrl, lang, {
          logger: (m) => {
            if (m.status === 'recognizing text' && m.progress != null) {
              setProgress((p) => ({ ...p, percent: Math.round(m.progress * 100) }));
            }
          }
        });

        // Plain text (preserve spacing from OCR; use tidy only lightly)
        const pageRaw = (result.data?.text || '').replace(/\r\n?/g, '\n');
        rawOut.push(`\n\n=== Page ${i}/${pdf.numPages} ===\n\n${pageRaw.trim()}`);
       
        // Structured → Markdown & JSON (per page)
        const struct = toStructured(result.data, {
        rowBandPx: 18,
        table: { minLines: 3 },
        kv: { preferOriginalKeys: true }
        });
        mdOut.push(`\n\n## Page ${i}\n\n${renderMarkdown(struct)}`);
        jsonOut.push({ page: i, struct });
      }

      setRawText(rawOut.join('\n').trim());
    } catch (e) {
      console.error(e);
      setError('Error extracting text.');
    } finally {
      setIsExtracting(false);
      setProgress((p) => ({ ...p, percent: 100 }));
    }
  }, [fileUrl, ocrScale, threshold, lang]);

  if (error) return <div>{error}</div>;

  const fit = zoom === 1;

  return (
    <div className={`pdf-container ${fit ? 'pdf-fit' : 'pdf-free'}`}>
      {/* 70%: PDF pane (scrollable) */}
      <div className="pdf-pane">
        <Document
          file={fileUrl}
          onLoadSuccess={onLoadSuccess}
          onLoadError={onLoadError}
          loading="Loading PDF..."
        >
          {numPages && [...Array(numPages).keys()].map((n) => (
            <div key={n} className="pdf-page-wrap">
              <Page
                pageNumber={n + 1}
                scale={zoom}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                renderMode="canvas"
              />
              <div className="pageNumberCount"><p>Page {n + 1} of {numPages}</p></div>
            </div>
          ))}
        </Document>
      </div>

      {/* 30%: OCR pane */}
      <div className='ocr-pane'>
        <div className="ocr-controls">
          <label>OCR render scale
            <input type="number" min="0.5" max="5" step="0.1"
                  value={ocrScale} onChange={(e)=>setOcrScale(parseFloat(e.target.value)||2.5)} />
          </label>
          <label>Threshold (0–255 or blank)
            <input type="number" min="0" max="255" placeholder="e.g. 180"
                  value={threshold}
                  onChange={(e)=>{ const v=e.target.value; setThreshold(v===''?'':Math.max(0,Math.min(255,+v||0))); }} />
          </label>
          <button className="ocr-button-1" type="button" onClick={runOCR} disabled={isExtracting || !numPages}>
            {isExtracting ? 'Extracting…' : 'Extract Text'}
          </button>
        </div>

        {isExtracting && (
          <div className="ocr-progress">
            Page {progress.page}/{progress.total} • {progress.percent}%
          </div>
        )}

        {/* Output */}
        {rawText ? (
          <div className="ocr-output-wrap">
            <div className="ocr-actions">
              <button 
                className="ocr-button-2" 
                onClick={(e)=>{
                  e.preventDefault();
                  navigator.clipboard.writeText(rawText)
                }}
              >
                Copy TXT
              </button>
              <button 
                className="ocr-button-2" 
                onClick={(e)=>{
                  e.preventDefault();
                const blob = new Blob([rawText], {type:'text/plain;charset=utf-8'});
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a'); a.href=url; a.download='extracted_text.txt'; a.click();
                URL.revokeObjectURL(url);
              }}>
                Download .txt
              </button>
            </div>
            <textarea className="ocr-output" rows={20} readOnly value={rawText} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
